import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DPSLayout } from '../../components/dps/layout/DPSLayout';
import { dpsTheme } from '../../styles/dpsTheme';
import {
  getSnapshotsByDiagnostic,
  getActionsByDiagnostic,
  getProgressStats,
  ImprovementAction,
  ScoreSnapshot,
  ProgressStats
} from '../../services/progressTrackingService';
import { ScoreEvolutionChart } from '../../components/dps/history/ScoreEvolutionChart';
import { TimelineView } from '../../components/dps/history/TimelineView';
import { GlobalStatsCards } from '../../components/dps/history/GlobalStatsCards';

type FilterType = 'all' | 'completed' | 'in_progress' | 'planned';

export const DPSHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState<ScoreSnapshot[]>([]);
  const [actions, setActions] = useState<ImprovementAction[]>([]);
  const [filteredActions, setFilteredActions] = useState<ImprovementAction[]>([]);
  const [stats, setStats] = useState<ProgressStats | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    applyFilter();
  }, [activeFilter, actions]);

  const loadData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [snapshotsData, actionsData, statsData] = await Promise.all([
        getSnapshotsByDiagnostic(id),
        getActionsByDiagnostic(id),
        getProgressStats(id)
      ]);

      setSnapshots(snapshotsData);
      setActions(actionsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading history data:', error);
    } finally {
      setLoading(false);
    }
  };

 const applyFilter = () => {
    const validActions = actions.filter(a => a.status !== null);

    if (activeFilter === 'all') {
      setFilteredActions(validActions.filter(a => a.status !== 'not_planned'));
    } else if (activeFilter === 'completed') {
      setFilteredActions(validActions.filter(a => a.status === 'completed'));
    } else if (activeFilter === 'in_progress') {
      setFilteredActions(validActions.filter(a => a.status === 'in_progress'));
    } else if (activeFilter === 'planned') {
      setFilteredActions(validActions.filter(a => a.status === 'planned'));
    }
  };

  const filters = [
    { id: 'all' as FilterType, label: 'Toutes' },
    { id: 'completed' as FilterType, label: 'Réalisées' },
    { id: 'in_progress' as FilterType, label: 'En cours' },
    { id: 'planned' as FilterType, label: 'Planifiées' }
  ];

  if (loading) {
    return (
      <DPSLayout reportId={id || ''}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ color: dpsTheme.colors.text.muted }}>Chargement de l'historique...</div>
        </div>
      </DPSLayout>
    );
  }

  return (
    <DPSLayout reportId={id || ''}>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6 lg:mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
              Historique de progression
            </h1>
            <p className="text-base sm:text-lg text-gray-400">
              Suivez l'évolution de votre sécurité dans le temps
            </p>
          </div>

          <div
            className="flex gap-2 backdrop-blur-xl border border-gray-700/50 p-1 rounded-lg overflow-x-auto"
            style={{ background: dpsTheme.colors.background.card }}
          >
            {filters.map(filter => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-all"
                style={{
                  color: activeFilter === filter.id ? dpsTheme.colors.accent.blue.secondary : dpsTheme.colors.text.muted,
                  background: activeFilter === filter.id ? dpsTheme.colors.accent.blue.bg : 'transparent',
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <ScoreEvolutionChart snapshots={snapshots} stats={stats} />
        <TimelineView actions={filteredActions} />
        <GlobalStatsCards stats={stats} />
      </main>
    </DPSLayout>
  );
};
